import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Store, ArrowRight, Sparkles, ShieldCheck, Truck, Clock, ChevronDown } from "lucide-react";

const SERVER_URL = "https://souqi-api.onrender.com"; 
const API_URL = `${SERVER_URL}/api`;

export default function Home() {
  const [merchants, setMerchants] = useState([]);
  const [loadingMerchants, setLoadingMerchants] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/merchants/public`)
      .then((res) => res.json())
      .then((data) => {
        setMerchants(data.merchants || []);
        setLoadingMerchants(false);
      })
      .catch((err) => {
        console.error("Erreur chargement marchands:", err);
        setLoadingMerchants(false);
      });
  }, []);

  const getMerchantLogo = (logo, width = 400) => {
    if (!logo) return null;
    let url = (logo.startsWith("http") || logo.startsWith("data:")) 
      ? logo 
      : `${SERVER_URL}${logo.startsWith("/") ? "" : "/"}${logo}`;

    if (url.includes("res.cloudinary.com")) {
      return url.replace("/upload/", `/upload/f_auto,q_auto,w_${width},c_limit/`);
    }
    return url;
  };

  return (
    <div className="min-h-screen font-sans bg-[#FFFFFF] selection:bg-red-100 selection:text-[#f34141]">
      
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-float { animation: float 3s ease-in-out infinite; }

        .glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
      `}</style>

      {/* ================= HERO SECTION ================= */}
      <section className="relative flex flex-col items-center justify-center min-h-[95vh] px-6 overflow-hidden">
        
        {/* Background Blobs */}
        <div className="absolute top-[-10%] left-[-5%] w-[40rem] h-[40rem] bg-red-50 rounded-full mix-blend-multiply filter blur-[120px] opacity-60 animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-5%] w-[35rem] h-[35rem] bg-orange-50 rounded-full mix-blend-multiply filter blur-[120px] opacity-60"></div>

        <div className="relative z-10 w-full max-w-5xl text-center">
          {/* Badge Partenaires */}
          {!loadingMerchants && (
            <div className="inline-flex items-center gap-3 px-6 py-2.5 mb-10 text-sm font-black text-white bg-gradient-to-r from-[#f34141] to-orange-500 rounded-full shadow-[0_10px_20px_rgba(243,65,65,0.3)] animate-bounce">
              <Store size={18} />
              <span className="tracking-tight">{merchants.length} Supermarchés Partenaires</span>
            </div>
          )}

          <h1 className="text-7xl md:text-[9rem] font-[1000] tracking-tighter text-slate-900 leading-[0.85] mb-10">
            Faites vos courses <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#f34141] via-rose-500 to-orange-500">
              sans limites.
            </span>
          </h1>

          {/* Espace Positif */}
          <div className="flex flex-wrap justify-center gap-6 mb-16">
            {[
              { icon: <ShieldCheck className="text-emerald-500" />, text: "Paiement Sécurisé" },
              { icon: <Truck className="text-blue-500" />, text: "Livraison Rapide" },
              { icon: <Clock className="text-orange-500" />, text: "Service 24/7" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-3 glass-card rounded-2xl shadow-sm border border-gray-100">
                {item.icon}
                <span className="text-sm font-black text-slate-700 tracking-tight">{item.text}</span>
              </div>
            ))}
          </div>

          {/* Bouton Explorer */}
          <button 
            onClick={() => document.getElementById('partenaires').scrollIntoView({ behavior: 'smooth' })}
            className="group relative inline-flex flex-col items-center gap-4 transition-transform active:scale-95"
          >
            <div className="flex items-center gap-3 px-10 py-5 bg-slate-900 text-white rounded-[2rem] text-xl font-black shadow-2xl group-hover:bg-[#f34141] transition-all duration-500">
              Explorer les rayons
              <ArrowRight className="group-hover:translate-x-2 transition-transform" />
            </div>
            <div className="flex flex-col items-center gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
              <span className="text-[10px] font-black uppercase tracking-[0.3em]">Scrollez pour voir</span>
              <ChevronDown size={24} className="animate-float" />
            </div>
          </button>
        </div>
      </section>

      {/* ================= STORES SECTION ================= */}
      <section id="partenaires" className="max-w-7xl mx-auto px-6 py-32">
        <div className="flex flex-col items-center text-center mb-20">
          <div className="flex items-center gap-2 mb-6 px-4 py-1.5 bg-red-50 rounded-full text-[#f34141]">
            <Sparkles size={16} />
            <span className="text-xs font-black uppercase tracking-widest">Le meilleur de Nouakchott</span>
          </div>
          {/* ✅ Modification : Nos Partenaires */}
          <h2 className="text-5xl md:text-6xl font-[1000] text-slate-900 tracking-tighter mb-4">
            Nos Partenaires
          </h2>
          <div className="w-24 h-2 bg-gradient-to-r from-[#f34141] to-orange-400 rounded-full"></div>
        </div>

        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {loadingMerchants ? (
            Array(3).fill(0).map((_, i) => (
              <div key={i} className="h-96 bg-gray-50 rounded-[3rem] animate-pulse"></div>
            ))
          ) : (
            merchants.map((m, index) => (
              <Link
                key={m._id}
                to={`/boutique/${m._id}`}
                className="group relative flex flex-col bg-white border border-gray-100 p-8 rounded-[3rem] transition-all duration-700 hover:shadow-[0_40px_80px_rgba(0,0,0,0.06)] hover:-translate-y-4"
              >
                <div className="relative aspect-square mb-8 overflow-hidden bg-[#FBFBFB] rounded-[2.5rem] flex items-center justify-center p-12 transition-colors group-hover:bg-white">
                  {getMerchantLogo(m.logo) ? (
                    <img
                      src={getMerchantLogo(m.logo, 400)}
                      alt={m.name}
                      className="object-contain w-full h-full transition-transform duration-1000 group-hover:scale-110"
                    />
                  ) : (
                    <Store size={64} className="text-gray-200" />
                  )}
                  <div className="absolute inset-0 bg-[#f34141]/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="px-6 py-3 bg-white text-[#f34141] font-black rounded-2xl shadow-xl transform translate-y-4 group-hover:translate-y-0 transition-transform">
                      Voir le catalogue
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <h3 className="text-3xl font-black text-slate-900 mb-3 group-hover:text-[#f34141] transition-colors">
                    {m.name}
                  </h3>
                  <p className="text-gray-400 font-bold text-sm uppercase tracking-wide">
                    Partenaire Officiel
                  </p>
                </div>
              </Link>
            ))
          )}
        </div>
      </section>

      {/* ================= ✅ WHATSAPP FLOATING (RETOUR AU VERT + TEXTE) ================= */}
      <button
        onClick={() => window.open("https://wa.me/22243269317", "_blank")}
        className="fixed bottom-10 right-8 z-50 flex items-center gap-3 px-6 py-4 bg-[#25D366] text-white rounded-full shadow-[0_15px_30px_rgba(37,211,102,0.4)] transition-all duration-300 hover:scale-110 active:scale-95 group"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="font-black tracking-tight">Besoin d'aide ?</span>
      </button>

    </div>
  );
}
